import { AnimatePresence, motion } from 'framer-motion';
import {
  BotIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  CoinsIcon,
  CopyIcon,
  InfoIcon,
  SearchIcon,
  UsersIcon,
  ZapIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import {
  CandidateCard,
  CandidateCardActions,
  CandidateCardContent,
  CandidateCardHeader,
  CandidateCardTitle,
} from '@/modules/candidates/components/CandidateCard';
import CandidateScore from '@/modules/candidates/components/CandidateScore';
import { SkillBadge } from '@/modules/candidates/components/SkillBadge';
import { WorkExperienceTimeline } from '@/modules/candidates/components/WorkExperienceTimeline';
import {
  CANDIDATE_JOBS_MAX_LIMIT,
  useCandidateJobsQuery,
} from '@/modules/candidates/queries/useCandidateJobsQuery';
import { useJobSourcingDebugQuery } from '@/modules/debug/queries/useJobSourcingDebugQuery';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Separator } from '@/modules/ui/components/separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import type { components } from '@/types/apiSchema';

type DebugJobSourcingResponse =
  components['schemas']['DebugJobSourcingResponse'];
type DebugAiResponseTokenUsageResponse =
  components['schemas']['DebugAiResponseTokenUsageResponse'];
type SourcingStrategy = components['schemas']['SourcingStrategy'];

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function tryFormatJson(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function computeTotalTokens(
  usage: DebugAiResponseTokenUsageResponse | null | undefined,
): { input: number; output: number; total: number } {
  if (!usage) return { input: 0, output: 0, total: 0 };

  const input =
    (usage.gemini20FlashLiteInputTokens ?? 0) +
    (usage.gemini20FlashInputTokens ?? 0) +
    (usage.gemini25FlashInputTokens ?? 0) +
    (usage.gemini25FlashLiteInputTokens ?? 0) +
    (usage.gemini3FlashInputTokens ?? 0);

  const output =
    (usage.gemini20FlashLiteOutputTokens ?? 0) +
    (usage.gemini20FlashOutputTokens ?? 0) +
    (usage.gemini25FlashOutputTokens ?? 0) +
    (usage.gemini25FlashLiteOutputTokens ?? 0) +
    (usage.gemini3FlashOutputTokens ?? 0);

  return { input, output, total: input + output };
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  copyValue?: string | null;
  children: React.ReactNode;
}

function CollapsibleSection({
  title,
  icon,
  badge,
  defaultOpen = false,
  copyValue,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    if (!copyValue) return;
    navigator.clipboard.writeText(copyValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex w-full items-center bg-neutral-50 transition-colors hover:bg-neutral-100">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex flex-1 items-center gap-3 px-4 py-3 text-left"
        >
          <span className="text-muted-foreground">{icon}</span>
          <span className="flex-1 text-sm font-medium">{title}</span>
          {badge}
          <motion.span
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDownIcon className="text-muted-foreground size-4" />
          </motion.span>
        </button>
        {copyValue != null && (
          <button
            onClick={handleCopy}
            className="text-muted-foreground hover:text-foreground mr-3 rounded p-1 transition-colors"
            title="Copy to clipboard"
          >
            {copied ? (
              <CheckIcon className="size-4 text-green-600" />
            ) : (
              <CopyIcon className="size-4" />
            )}
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="border-t px-4 py-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface JsonBlockProps {
  value: string | null | undefined;
  maxHeight?: string;
}

function JsonBlock({ value, maxHeight = '400px' }: JsonBlockProps) {
  const formatted = tryFormatJson(value);
  if (!formatted) {
    return (
      <p className="text-muted-foreground text-sm italic">No data available</p>
    );
  }

  return (
    <pre
      className="overflow-auto rounded-md bg-neutral-900 p-4 text-xs leading-relaxed break-all whitespace-pre-wrap text-neutral-100"
      style={{ maxHeight }}
    >
      {formatted}
    </pre>
  );
}

interface TokenTableProps {
  usage: DebugAiResponseTokenUsageResponse | null | undefined;
}

function TokenTable({ usage }: TokenTableProps) {
  if (!usage) {
    return (
      <p className="text-muted-foreground text-sm italic">
        No token usage data
      </p>
    );
  }

  const models = [
    {
      name: 'Gemini 2.0 Flash Lite',
      input: usage.gemini20FlashLiteInputTokens,
      output: usage.gemini20FlashLiteOutputTokens,
    },
    {
      name: 'Gemini 2.0 Flash',
      input: usage.gemini20FlashInputTokens,
      output: usage.gemini20FlashOutputTokens,
    },
    {
      name: 'Gemini 2.5 Flash',
      input: usage.gemini25FlashInputTokens,
      output: usage.gemini25FlashOutputTokens,
    },
    {
      name: 'Gemini 2.5 Flash Lite',
      input: usage.gemini25FlashLiteInputTokens,
      output: usage.gemini25FlashLiteOutputTokens,
    },
    {
      name: 'Gemini 3 Flash',
      input: usage.gemini3FlashInputTokens,
      output: usage.gemini3FlashOutputTokens,
    },
  ].filter((m) => (m.input ?? 0) > 0 || (m.output ?? 0) > 0);

  if (models.length === 0) {
    return (
      <p className="text-muted-foreground text-sm italic">
        No token usage recorded
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="pr-4 pb-2 font-medium">Model</th>
            <th className="pr-4 pb-2 text-right font-medium">Input</th>
            <th className="pr-4 pb-2 text-right font-medium">Output</th>
            <th className="pb-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {models.map((model) => (
            <tr key={model.name} className="border-b last:border-0">
              <td className="py-2 pr-4">{model.name}</td>
              <td className="py-2 pr-4 text-right font-mono text-xs">
                {(model.input ?? 0).toLocaleString()}
              </td>
              <td className="py-2 pr-4 text-right font-mono text-xs">
                {(model.output ?? 0).toLocaleString()}
              </td>
              <td className="py-2 text-right font-mono text-xs">
                {((model.input ?? 0) + (model.output ?? 0)).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SourcingRunCardProps {
  run: DebugJobSourcingResponse;
  index: number;
  strategy?: SourcingStrategy | null;
}

function SourcingRunCard({ run, index, strategy }: SourcingRunCardProps) {
  const totals = computeTotalTokens(run.aiResponse?.tokenUsage);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" size="sm">
          Run #{index + 1}
        </Badge>
        {strategy && (
          <Badge
            variant={strategy === 'Agent' ? 'default' : 'outline'}
            size="sm"
          >
            <ZapIcon className="size-3" />
            {strategy}
          </Badge>
        )}
        {run.aiResponse?.model && (
          <Badge variant="outline" size="sm">
            {run.aiResponse.model}
          </Badge>
        )}
        {run.aiResponse?.task && (
          <Badge variant="outline" size="sm">
            {run.aiResponse.task}
          </Badge>
        )}
        {run.totalResults != null && (
          <Badge variant="outline" size="sm">
            {run.totalResults} results
          </Badge>
        )}
        {totals.total > 0 && (
          <Badge variant="outline" size="sm">
            <CoinsIcon className="size-3" />
            {totals.total.toLocaleString()} tokens
          </Badge>
        )}
      </div>

      <div className="text-muted-foreground flex flex-wrap gap-4 text-xs">
        <span className="flex items-center gap-1">
          <ClockIcon className="size-3" />
          Started: {formatDateTime(run.startedAt)}
        </span>
        <span className="flex items-center gap-1">
          <ClockIcon className="size-3" />
          Finished: {formatDateTime(run.finishedAt)}
        </span>
        {run.agentSessionId && <span>Session: {run.agentSessionId}</span>}
      </div>

      <CollapsibleSection
        title="Search Query"
        icon={<SearchIcon className="size-4" />}
        copyValue={tryFormatJson(run.searchQuery)}
      >
        <JsonBlock value={run.searchQuery} />
      </CollapsibleSection>

      {run.aiResponse && (
        <>
          <CollapsibleSection
            title="AI Prompt"
            icon={<BotIcon className="size-4" />}
            copyValue={tryFormatJson(run.aiResponse.prompt)}
          >
            <JsonBlock value={run.aiResponse.prompt} maxHeight="600px" />
          </CollapsibleSection>

          <CollapsibleSection
            title="AI Response"
            icon={<BotIcon className="size-4" />}
            copyValue={tryFormatJson(run.aiResponse.response)}
          >
            <JsonBlock value={run.aiResponse.response} maxHeight="600px" />
          </CollapsibleSection>

          <CollapsibleSection
            title="Events"
            icon={<BotIcon className="size-4" />}
            copyValue={tryFormatJson(run.aiResponse.events)}
          >
            <JsonBlock value={run.aiResponse.events} maxHeight="600px" />
          </CollapsibleSection>

          <CollapsibleSection
            title="Token Usage"
            icon={<CoinsIcon className="size-4" />}
          >
            <TokenTable usage={run.aiResponse.tokenUsage} />
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}

export function JobSourcingDebug() {
  const { jobId } = useParams<{ jobId: string }>();
  const jobQuery = useJobQuery(jobId!);
  const debugQuery = useJobSourcingDebugQuery(jobId!);
  const candidatesQuery = useCandidateJobsQuery({
    jobId,
    limit: CANDIDATE_JOBS_MAX_LIMIT,
  });

  const jobSourcings = debugQuery.data?.jobSourcings ?? [];
  const candidates = candidatesQuery.data?.items ?? [];

  const allTokens = jobSourcings.reduce(
    (acc, run) => {
      const t = computeTotalTokens(run.aiResponse?.tokenUsage);
      return {
        input: acc.input + t.input,
        output: acc.output + t.output,
        total: acc.total + t.total,
      };
    },
    { input: 0, output: 0, total: 0 },
  );

  const candidateRanking = {
    good: candidates.filter((c) => c.score != null && c.score >= 75).length,
    average: candidates.filter(
      (c) => c.score != null && c.score >= 50 && c.score < 75,
    ).length,
    bad: candidates.filter((c) => c.score != null && c.score < 50).length,
  };

  const isLoading = debugQuery.isLoading || jobQuery.isLoading;
  const pageTitle = jobQuery.data?.title
    ? `Debug: ${jobQuery.data.title}`
    : 'Sourcing Agent Debug';

  return (
    <>
      <DocumentMetadata title={pageTitle} />
      <Container>
        <PageHeader
          title={pageTitle}
          subtitle={
            jobQuery.data?.company && (
              <Link
                to={`/companies/${jobQuery.data.company.id}/edit`}
                className="hover:underline"
              >
                {jobQuery.data.company.name}
              </Link>
            )
          }
          action={
            <div className="flex items-center gap-2">
              <Button variant="outline" asChild>
                <Link to={`/jobs/${jobId}/edit`}>Edit Job</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to={`/jobs/${jobId}/candidates`}>Back to Candidates</Link>
              </Button>
            </div>
          }
        />

        {isLoading ? (
          <div className="py-20">
            <Loader />
          </div>
        ) : debugQuery.error ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-sm text-red-600">
                Failed to load debug data. Make sure you have the required
                permissions.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6 pb-8">
            {/* Summary */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                label="Strategy"
                value={jobQuery.data?.sourcingStrategy ?? '—'}
                icon={<ZapIcon className="size-5" />}
              />
              <SummaryCard
                label="Sourcing Runs"
                value={jobSourcings.length}
                icon={<SearchIcon className="size-5" />}
              />
              <SummaryCard
                label="Total Tokens"
                value={allTokens.total.toLocaleString()}
                icon={<CoinsIcon className="size-5" />}
                tooltip="Token usage is only tracked for the Sourcing Agent strategy"
              />
              <SummaryCard
                label="Input Tokens"
                value={allTokens.input.toLocaleString()}
                icon={<CoinsIcon className="size-5" />}
                tooltip="Token usage is only tracked for the Sourcing Agent strategy"
              />
            </div>

            {/* Candidate ranking */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                label="Candidates"
                value={candidates.length}
                icon={<UsersIcon className="size-5" />}
              />
              <SummaryCard
                label="Good (score ≥ 75)"
                value={candidateRanking.good}
                icon={<UsersIcon className="size-5" />}
                accent="green"
              />
              <SummaryCard
                label="Average (score 50–74)"
                value={candidateRanking.average}
                icon={<UsersIcon className="size-5" />}
                accent="yellow"
              />
              <SummaryCard
                label="Poor (score < 50)"
                value={candidateRanking.bad}
                icon={<UsersIcon className="size-5" />}
                accent="red"
              />
            </div>

            {/* Sourcing Runs */}
            <Card>
              <CardHeader>
                <CardTitle>Sourcing Runs ({jobSourcings.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {jobSourcings.length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    No sourcing runs found for this job.
                  </p>
                ) : (
                  <div className="space-y-8">
                    {jobSourcings.map((run, index) => (
                      <div key={run.id ?? index}>
                        {index > 0 && <div className="mb-8 border-t" />}
                        <SourcingRunCard
                          run={run}
                          index={index}
                          strategy={jobQuery.data?.sourcingStrategy}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Candidates */}
            <Card>
              <CardHeader>
                <CardTitle>Candidates ({candidates.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {candidates.length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    No candidates found for this job.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {candidates.map((cj) => {
                      const { candidate, status, strengths, clarifications } =
                        cj;

                      return (
                        <CandidateCard key={cj.id}>
                          <CandidateCardHeader>
                            <CandidateCardTitle
                              candidate={candidate}
                              candidateStatus={status}
                              linkTo={`/jobs/${jobId}/candidates/${candidate.id}`}
                            />
                            <CandidateCardActions>
                              <CandidateScore score={cj.score} />
                            </CandidateCardActions>
                          </CandidateCardHeader>

                          <Separator />

                          <CandidateCardContent>
                            <div className="flex flex-col gap-6 md:flex-row md:gap-8">
                              <div className="flex-1">
                                {candidate.workExperiences ? (
                                  <WorkExperienceTimeline
                                    workExperiences={candidate.workExperiences}
                                  />
                                ) : (
                                  <Paragraph variant="caption">
                                    No work experience available
                                  </Paragraph>
                                )}
                              </div>

                              <div className="bg-border -my-4 hidden w-px self-stretch md:-my-5 md:block" />

                              <div className="flex flex-1 flex-col gap-6">
                                <div className="flex flex-wrap gap-2">
                                  {strengths?.map((strength, index) => (
                                    <SkillBadge
                                      key={`strength-${index}`}
                                      isMustHave
                                      summary={strength.summary}
                                      explanation={strength.explanation}
                                    />
                                  ))}
                                  {clarifications?.map(
                                    (clarification, index) => (
                                      <SkillBadge
                                        key={`clarification-${index}`}
                                        isMustHave={false}
                                        summary={clarification.summary}
                                        explanation={clarification.explanation}
                                      />
                                    ),
                                  )}
                                  {!strengths?.length &&
                                    !clarifications?.length && (
                                      <Paragraph variant="caption">
                                        No skills data available
                                      </Paragraph>
                                    )}
                                </div>
                              </div>
                            </div>
                          </CandidateCardContent>
                        </CandidateCard>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </Container>
    </>
  );
}

interface SummaryCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  accent?: 'green' | 'yellow' | 'red';
  tooltip?: string;
}

function SummaryCard({
  label,
  value,
  icon,
  accent,
  tooltip,
}: SummaryCardProps) {
  const accentStyles = accent
    ? {
        green: {
          iconBg: 'bg-green-100 text-green-600',
          value: 'text-green-600',
        },
        yellow: {
          iconBg: 'bg-yellow-100 text-yellow-600',
          value: 'text-yellow-600',
        },
        red: { iconBg: 'bg-red-100 text-red-600', value: 'text-red-600' },
      }[accent]
    : null;

  const iconBg = accentStyles?.iconBg ?? 'bg-neutral-100 text-muted-foreground';
  const valueColor = accentStyles?.value ?? '';

  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-4">
        <div className={cn('rounded-lg p-2', iconBg)}>{icon}</div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-muted-foreground text-xs">{label}</p>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="text-muted-foreground size-3 cursor-default" />
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className={cn('text-xl font-semibold', valueColor)}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
